import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { HiOutlineShoppingBag } from 'react-icons/hi';

/**
 * Bottom-center floating cart button — appears only on a store's own
 * pages (the menu page and product pages) when the basket has items.
 * Gives customers quick access to the cart while they keep browsing.
 *
 * Visibility is gated on real store context: the current path must sit
 * under the active store's slug. That excludes /home, /terms, /privacy,
 * /login, /forgot-password and /user/* outright — none of them are a
 * store page. Within a store, the checkout-flow sub-routes (checkout,
 * payment, success, login, complete) still hide it, since there the
 * cart is either redundant or already the primary surface.
 */
const FloatingCart = ({ onClick }) => {
  const basket = useSelector((state) => state.basket.basket);
  const currentStore = useSelector((state) => state.base.common.currentStore);
  const { pathname } = useLocation();

  const qty = basket.items?.reduce((sum, item) => sum + (item.qty || 0), 0) || 0;

  // Only show while on the active store's own pages. currentStore.slug is
  // set to the URL slug by the store view, so this is false on /home and
  // every other non-store route.
  const slug = currentStore?.slug;
  const onStorePath =
    !!slug && (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`));

  // Within a store, these sub-routes are not the menu page — hide there.
  const hiddenSubRoutes = [
    /\/checkout(\/|$)/,
    /\/payment(\/|$)/,
    /\/success(\/|$)/,
    /\/login(\/|$)/,
    /\/complete(\/|$)/,
  ];
  const onHiddenSubRoute = hiddenSubRoutes.some((r) => r.test(pathname));
  const visible = qty > 0 && onStorePath && !onHiddenSubRoute;

  return (
    <div
      className="fixed left-0 right-0 z-40 flex justify-center px-4"
      style={{
        bottom: 'calc(var(--sab) + 16px)',
        transform: visible ? 'translateY(0)' : 'translateY(160%)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s',
        willChange: 'transform, opacity',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={`Your cart: ${qty} item${qty !== 1 ? 's' : ''}`}
        className="flex items-center justify-between transition-transform hover:-translate-y-[2px] w-full sm:w-auto"
        style={{
          maxWidth: '440px',
          padding: '8px 8px 8px 18px',
          borderRadius: '14px',
          gap: '14px',
          background: 'var(--ui-ink)',
          color: '#FFFFFF',
          boxShadow:
            '0 24px 60px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.06)',
          cursor: 'pointer',
          border: 'none',
          fontFamily: 'inherit',
        }}
      >
        <span
          className="inline-flex items-center gap-[10px]"
          style={{ fontSize: '13px', fontWeight: 600 }}
        >
          <span
            className="inline-flex items-center justify-center font-extrabold"
            style={{
              minWidth: '22px',
              height: '22px',
              padding: '0 6px',
              borderRadius: '6px',
              background: 'var(--action-green)',
              color: 'var(--action-green-fg)',
              fontSize: '11px',
              fontFamily: 'Geist Mono, monospace',
            }}
          >
            {qty}
          </span>
          Your cart
        </span>

        <span
          className="inline-flex items-center gap-[8px]"
          style={{
            padding: '9px 14px',
            borderRadius: '10px',
            background: 'var(--action-green)',
            color: 'var(--action-green-fg)',
            fontSize: '13px',
            fontWeight: 700,
          }}
        >
          {qty} {qty === 1 ? 'Item' : 'Items'}
          <HiOutlineShoppingBag size={14} />
        </span>
      </button>
    </div>
  );
};

export default FloatingCart;
